import { z } from 'zod';

/**
 * Zod schemas for purchase order-related entities
 * These provide runtime validation and type inference
 */

// POLineItem schema
const poLineItemSchema = z.object({
  id: z.string(),
  description: z.string().min(1, 'Line item description is required'),
  quantity: z.number().nonnegative(),
  unitPrice: z.number().nonnegative(),
  total: z.number().nonnegative(),
  receivedQuantity: z.number().nonnegative().optional(),
  notes: z.string().optional(),
});

// PurchaseOrder schema
export const purchaseOrderSchema = z.object({
  id: z.string(),
  poNumber: z.string().min(1, 'PO number is required'),
  vendorId: z.string(),
  vendor: z.string(),
  propertyId: z.string(),
  property: z.string(),
  date: z.string(),
  expectedDeliveryDate: z.string().optional(),
  status: z.enum(['draft', 'sent', 'acknowledged', 'received', 'cancelled'] as const),
  lineItems: z.array(poLineItemSchema),
  totalAmount: z.number().nonnegative(),
  description: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});

export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

// MatchDiscrepancy schema
const matchDiscrepancySchema = z.object({
  type: z.enum(['quantity', 'price', 'item_missing', 'item_extra'] as const),
  field: z.string(),
  poValue: z.union([z.number(), z.string(), z.null()]),
  invoiceValue: z.union([z.number(), z.string(), z.null()]),
  receiptValue: z.union([z.number(), z.string(), z.null()]).optional(),
  severity: z.enum(['warning', 'error'] as const),
  description: z.string(),
});

// ThreeWayMatch schema
export const threeWayMatchSchema = z.object({
  id: z.string(),
  purchaseOrderId: z.string(),
  invoiceId: z.string(),
  receiptId: z.string().optional(),
  status: z.enum(['pending', 'matched', 'discrepancy', 'approved'] as const),
  discrepancies: z.array(matchDiscrepancySchema),
  matchedAt: z.string().optional(),
  approvedBy: z.string().optional(),
  approvedAt: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});

export type ThreeWayMatch = z.infer<typeof threeWayMatchSchema>;
